import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { MoveHorizontalIcon } from 'lucide-react';
const BEFORE_IMAGE = 'https://cdn.magicpatterns.com/uploads/53rN54pxtEJYmQFBww5ePD/Screenshot_2026-03-13_162652.png';
const AFTER_IMAGE = 'https://cdn.magicpatterns.com/uploads/cxkiUqx4Shw7K2QyNik2RB/Screenshot_2026-03-13_162701.png';
interface ComparisonSliderProps {
  beforeImage: string;
  afterImage: string;
  beforeLabel?: string;
  afterLabel?: string;
}
function ComparisonSlider({
  beforeImage,
  afterImage,
  beforeLabel = 'BEFORE',
  afterLabel = 'AFTER'
}: ComparisonSliderProps) {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const handleMove = (clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    setSliderPosition(x / rect.width * 100);
  };
  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (isDragging) handleMove(e.clientX);
    };
    const onTouchMove = (e: TouchEvent) => {
      if (isDragging) handleMove(e.touches[0].clientX);
    };
    const stop = () => setIsDragging(false);
    if (isDragging) {
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', stop);
      window.addEventListener('touchmove', onTouchMove, {
        passive: true
      });
      window.addEventListener('touchend', stop);
    }
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', stop);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', stop);
    };
  }, [isDragging]);
  return <div ref={containerRef} className="relative w-full rounded-xl overflow-hidden cursor-ew-resize select-none" style={{
    aspectRatio: '4/3',
    boxShadow: '0 20px 60px rgba(0,0,0,0.4), 0 8px 20px rgba(0,0,0,0.3)'
  }} onMouseDown={e => {
    setIsDragging(true);
    handleMove(e.clientX);
  }} onTouchStart={e => {
    setIsDragging(true);
    handleMove(e.touches[0].clientX);
  }} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} data-id="element-14">
      {/* After image (base layer) */}
      <img src={afterImage} alt="After cleaning" className="absolute inset-0 w-full h-full object-cover object-center" draggable={false} data-id="element-15" />

      {/* Before image (clipped) */}
      <div className="absolute inset-0" style={{
      clipPath: `polygon(0 0, ${sliderPosition}% 0, ${sliderPosition}% 100%, 0 100%)`
    }} data-id="element-16">
        <img src={beforeImage} alt="Before cleaning" className="absolute inset-0 w-full h-full object-cover object-center" draggable={false} data-id="element-17" />
      </div>

      {/* Labels */}
      <div className="absolute top-4 left-4 z-20 pointer-events-none" data-id="element-18">
        <span className="bg-black/70 backdrop-blur-sm text-white text-xs font-black px-3 py-1.5 rounded-full tracking-widest uppercase border border-white/20" data-id="element-19">
          {beforeLabel}
        </span>
      </div>
      <div className="absolute top-4 right-4 z-20 pointer-events-none" data-id="element-20">
        <span className="bg-[#7AC943]/90 backdrop-blur-sm text-white text-xs font-black px-3 py-1.5 rounded-full tracking-widest uppercase border border-white/20" data-id="element-21">
          {afterLabel}
        </span>
      </div>

      {/* Divider line — bright yellow */}
      <div className="absolute top-0 bottom-0 z-10 pointer-events-none" style={{
      left: `${sliderPosition}%`,
      transform: 'translateX(-50%)',
      width: '3px',
      background: '#FFD400',
      boxShadow: '0 0 12px rgba(255,212,0,0.8), 0 0 24px rgba(255,212,0,0.4)'
    }} data-id="element-22" />

      {/* Slider handle */}
      <div className="absolute top-1/2 z-20 -translate-y-1/2 -translate-x-1/2 transition-transform duration-150" style={{
      left: `${sliderPosition}%`,
      transform: `translate(-50%, -50%) scale(${isDragging ? 1.15 : isHovered ? 1.08 : 1})`
    }} data-id="element-23">
        <div className="w-12 h-12 rounded-full flex items-center justify-center border-2 border-white" style={{
        background: '#FFD400',
        boxShadow: isDragging ? '0 0 0 6px rgba(255,212,0,0.35), 0 4px 20px rgba(0,0,0,0.4)' : isHovered ? '0 0 0 4px rgba(255,212,0,0.25), 0 4px 16px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.3)',
        transition: 'box-shadow 0.2s ease'
      }} data-id="element-24">
          <MoveHorizontalIcon className="w-5 h-5 text-[#0A1628]" strokeWidth={2.5} data-id="element-25" />
        </div>
      </div>
    </div>;
}
const COMPARISONS = [{
  before: BEFORE_IMAGE,
  after: AFTER_IMAGE,
  label: 'Power Washing — Siding & Exterior'
}, {
  before: 'https://cdn.magicpatterns.com/uploads/nWmUnQsAkGk5hXqPy5kiWV/Screenshot_2026-03-13_171234.png',
  after: 'https://cdn.magicpatterns.com/uploads/pqGa1VqUGrMaEhiE7JvhA4/Screenshot_2026-03-13_171240.png',
  label: 'Roof & Gutter Debris Removal'
}, {
  before: 'https://cdn.magicpatterns.com/uploads/bC62uFcK4v9uUZxoPSZwdW/Screenshot_2026-03-13_171841.png',
  after: 'https://cdn.magicpatterns.com/uploads/kRq8nrgPUYMsTad99N5fjC/Screenshot_2026-03-13_171848.png',
  label: 'Flat Roof Drain Cleaning'
}];
export function BeforeAfter() {
  return <section id="before-after" className="relative py-24 bg-[#0A1628] overflow-hidden" data-id="element-26">
      {/* Background glow */}
      <div className="absolute inset-0 pointer-events-none" style={{
      background: 'radial-gradient(ellipse at 50% 0%, rgba(11,122,196,0.15) 0%, transparent 60%)'
    }} data-id="element-27" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-28">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7
      }} className="text-center mb-16" data-id="element-29">
          <p className="text-[#FFD400] font-bold uppercase tracking-widest text-sm mb-3" data-id="element-30">
            Real Results
          </p>
          <h2 className="text-4xl md:text-5xl font-black text-white mb-4" data-id="element-31">
            See The Difference
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-[#FFD400] to-[#0B7AC4] mx-auto rounded-full mb-6" data-id="element-32" />
          <p className="text-xl text-gray-400 max-w-2xl mx-auto font-light" data-id="element-33">
            Real results from NJ Four Seasons Gutter Cleaning.{' '}
            <span className="text-gray-300 font-medium" data-id="element-34">
              Drag the slider to reveal the transformation.
            </span>
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-35">
          {COMPARISONS.map((comp, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 32
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.15,
          duration: 0.6,
          ease: [0.22, 1, 0.36, 1]
        }} className="flex flex-col gap-4" data-id="element-36">
              <ComparisonSlider beforeImage={comp.before} afterImage={comp.after} data-id="element-37" />
              <div className="flex items-center justify-between px-1" data-id="element-38">
                <span className="text-gray-400 text-sm font-medium" data-id="element-39">
                  {comp.label}
                </span>
                <span className="text-[#7AC943] text-xs font-bold uppercase tracking-wider" data-id="element-40">
                  ✓ Completed
                </span>
              </div>
            </motion.div>)}
        </div>

        {/* Bottom CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.5
      }} className="mt-16 text-center" data-id="element-41">
          <p className="text-gray-400 mb-6 text-lg" data-id="element-42">
            Ready to transform your home?
          </p>
          <a href="#contact" className="btn-primary inline-flex items-center gap-2 text-lg" data-id="element-43">
            Get Your Free Estimate Today
          </a>
        </motion.div>
      </div>
    </section>;
}